import type { CSSProperties, ReactNode } from 'react'

import { Children } from 'react'
import { cn } from '@/lib/utils'

type MarqueeProps = {
    children: ReactNode
    /** Seconds for one full loop. Lower is faster. */
    duration?: number
    reverse?: boolean
    pauseOnHover?: boolean
    gap?: string
    className?: string
    trackClassName?: string
    /** Fades both edges into the page background. */
    fade?: boolean
}

type TrackStyle = CSSProperties & {
    '--marquee-duration': string
    '--marquee-direction': string
    '--marquee-hover-state': string
}

/**
 * Infinite horizontal ticker used by the client logos, the results row and the
 * team row. The children are rendered twice and the track travels exactly half
 * its width, so the seam is never visible.
 */
export const Marquee = ({
    children,
    duration = 40,
    reverse = false,
    pauseOnHover = true,
    gap = '24px',
    className,
    trackClassName,
    fade = true
}: MarqueeProps) => {
    const items = Children.toArray(children)

    const style: TrackStyle = {
        '--marquee-duration': `${duration}s`,
        '--marquee-direction': reverse ? 'reverse' : 'normal',
        '--marquee-hover-state': pauseOnHover ? 'paused' : 'running'
    }

    return (
        <div
            className={cn(
                'marquee-viewport relative flex w-full overflow-hidden',
                fade && 'marquee-mask',
                className
            )}
        >
            <div
                className={cn('marquee-track items-center', trackClassName)}
                style={style}
            >
                {items.map((item, index) => (
                    <div
                        key={`a-${index}`}
                        className='shrink-0'
                        style={{ marginRight: gap }}
                    >
                        {item}
                    </div>
                ))}

                {items.map((item, index) => (
                    <div
                        key={`b-${index}`}
                        className='shrink-0'
                        style={{ marginRight: gap }}
                        aria-hidden='true'
                    >
                        {item}
                    </div>
                ))}
            </div>
        </div>
    )
}
